import { useState, useEffect, useRef } from 'react';
import { useSubscription } from '@apollo/client';
import { SUBSCRIPTION_STREAM_CHAT_MESSAGE } from '@/graphql/subscription/chat';
import { ChatStreamChunk, ChatStreamInput } from '@/graphql/types/chat';

interface UseChatStreamParams {
  input: ChatStreamInput | null;
  onComplete?: (fullResponse: string, conversationId: string) => void;
  onError?: (error: string) => void;
}

interface UseChatStreamReturn {
  fullResponse: string;
  isStreaming: boolean;
  error: string | null;
  conversationId: string | null;
}

export const useChatStream = ({
  input,
  onComplete,
  onError,
}: UseChatStreamParams): UseChatStreamReturn => {
  const [fullResponse, setFullResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  useSubscription<{
    streamChatMessage: ChatStreamChunk;
  }>(SUBSCRIPTION_STREAM_CHAT_MESSAGE, {
    variables: { input },
    skip: !input,
    onData: ({ data: subscriptionData }) => {
      const chunk = subscriptionData.data?.streamChatMessage;

      if (!chunk) return;

      if (chunk.conversation_id && !conversationId) {
        setConversationId(chunk.conversation_id);
      }

      if (!chunk.is_complete) {
        setFullResponse((prev) => prev + chunk.chunk);
        setIsStreaming(true);
        setError(null);
      } else {
        setIsStreaming(false);

        if (chunk.error) {
          setError(chunk.error);
          if (onErrorRef.current) {
            onErrorRef.current(chunk.error);
          }
        } else if (onCompleteRef.current) {
          onCompleteRef.current(fullResponse + chunk.chunk, chunk.conversation_id);
        }
      }
    },
    onError: (err) => {
      const errorMessage = err.message || 'WebSocket connection failed';
      setError(errorMessage);
      setIsStreaming(false);

      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    },
  });

  useEffect(() => {
    if (input) {
      setFullResponse('');
      setError(null);
      setIsStreaming(true);
    }
  }, [input]);

  return {
    fullResponse,
    isStreaming,
    error,
    conversationId,
  };
};
